import { keynoteConfig, pluginConfig, playerList, AiEmList } from "./data";
import {
    getEmketNote,
    getEmTune,
    getEmRate,
    getEmHumanize,
    getEmFlex,
    getEmVibrato,
    getEmTracking,
    getRemoveVocals,
    getInputType,
} from "./formatParams";
import {
    cppUpdateKeynote,
    cppSendRemoteMessageKeynote,
    cppSendRemoteMessageTune,
    cppSendRemoteMessageSpeed,
    cppSendRemoteMessageHumanize,
    cppSendRemoteMessageFlex,
    cppSendRemoteMessageVibrato,
    cppSendRemoteMessageTracking,
    cppSendRemoteMessageInputType,
    cppSendRemoteMessageVocals,
    cppSendRemoteMessageMultiple,
    cppSendRemoteMessageEmValue,
} from "../../api/cpp";

const createSerialNum = createCounter();
/**
 * 更新基调信息到插件 通过升降调计算出最终要更新的基调信息
 * @param {Object} keynote
 * @param {Number} sharpFlatVal
 * @param {Number} emSharpFlatVal
 * @param {Boolean} isEnableAutotune
 * @returns
 */
export function setUpdateKeynote(
    keynote,
    sharpFlatVal,
    emSharpFlatVal,
    isEnableAutotune,
    emPlugsV3
) {
    if (
        !keynote ||
        !keynote.newKeyNote ||
        !keynote.newScale ||
        !isEnableAutotune ||
        !emPlugsV3
    ) {
        return false;
    }
    // AI分析的基调不受升降调影响
    if (keynote.type === "EM_KN_AI") {
        sharpFlatVal = 0;
    }
    const keyNoteObj = computeEmKeyNote(keynote, sharpFlatVal, emSharpFlatVal);
    const params = getEmketNote(
        emPlugsV3,
        keyNoteObj.emKeynote,
        keyNoteObj.emKey
    );
    const serialNum = createSerialNum();
    cppSendRemoteMessageKeynote(params, serialNum);
    cppUpdateKeynote(keyNoteObj.musicKeynote);
}

// 同时设置基调和升降调
export function setUpdateKeynoteAndTune(
    keynote,
    sharpFlatVal,
    emSharpFlatVal,
    isEnableAutotune,
    emPlugsV3
) {
    if (
        !keynote ||
        !keynote.newKeyNote ||
        !keynote.newScale ||
        !isEnableAutotune ||
        !emPlugsV3
    ) {
        return false;
    }
    const keyNoteObj = computeEmKeyNote(keynote, sharpFlatVal, emSharpFlatVal);
    const emParams = getEmketNote(
        emPlugsV3,
        keyNoteObj.emKeynote,
        keyNoteObj.emKey
    );
    const tuneParams = getEmTune(emPlugsV3, sharpFlatVal);
    const params = emParams.concat(tuneParams);
    const serialNum = createSerialNum();
    cppSendRemoteMessageMultiple(params, serialNum);
    cppUpdateKeynote(keyNoteObj.musicKeynote);
}

/**
 * 设置升降调
 * @param {Object} emPlugsV3
 * @param {Number} tune
 * @returns
 */
export function setUpdateTune(emPlugsV3, tune) {
    if (!emPlugsV3 || typeof tune !== "number") {
        return false;
    }
    const params = getEmTune(emPlugsV3, tune);
    const serialNum = createSerialNum();
    cppSendRemoteMessageTune(params, serialNum);
}
// 更新速率信息
export function setUpdateRateValue(emPlugsV3, value) {
    if (!emPlugsV3 || typeof value !== "number") {
        return false;
    }
    const params = getEmRate(emPlugsV3, value);
    const serialNum = createSerialNum();
    cppSendRemoteMessageSpeed(params, serialNum);
}
// 更新拟人信息
export function setUpdateHumanizeValue(emPlugsV3, value) {
    if (!emPlugsV3 || typeof value !== "number") {
        return false;
    }
    const params = getEmHumanize(emPlugsV3, value);
    const serialNum = createSerialNum();
    cppSendRemoteMessageHumanize(params, serialNum);
}
// 更新弹性信息
export function setUpdateFlexValue(emPlugsV3, value) {
    if (!emPlugsV3 || typeof value !== "number") {
        return false;
    }
    const params = getEmFlex(emPlugsV3, value);
    const serialNum = createSerialNum();
    cppSendRemoteMessageFlex(params, serialNum);
}
// 更新颤音信息
export function setUpdateVibratoValue(emPlugsV3, value) {
    if (!emPlugsV3 || typeof value !== "number") {
        return false;
    }
    const params = getEmVibrato(emPlugsV3, value);
    const serialNum = createSerialNum();
    cppSendRemoteMessageVibrato(params, serialNum);
}
// 更新跟踪信息
export function setUpdateTrackingValue(emPlugsV3, value) {
    if (!emPlugsV3 || typeof value !== "number") {
        return false;
    }
    const params = getEmTracking(emPlugsV3, value);
    const serialNum = createSerialNum();
    cppSendRemoteMessageTracking(params, serialNum);
}
// 更新电音参数
export function setUpdateEmValues(emPlugsV3, valueObj) {
    if (!emPlugsV3 || !valueObj) {
        return false;
    }
    let rateParam = [];
    let humanizeParam = [];
    let flexParam = [];
    let vibratoParam = [];
    let trackingParam = [];
    if (typeof valueObj.rate === "number") {
        rateParam = getEmRate(emPlugsV3, valueObj.rate);
    }
    if (typeof valueObj.humanize === "number") {
        humanizeParam = getEmHumanize(emPlugsV3, valueObj.humanize);
    }
    if (typeof valueObj.flex === "number") {
        flexParam = getEmFlex(emPlugsV3, valueObj.flex);
    }
    if (typeof valueObj.vibrato === "number") {
        vibratoParam = getEmVibrato(emPlugsV3, valueObj.vibrato);
    }
    if (typeof valueObj.tracking === "number") {
        trackingParam = getEmTracking(emPlugsV3, valueObj.tracking);
    }
    const params = rateParam.concat(
        humanizeParam,
        flexParam,
        vibratoParam,
        trackingParam
    );
    if (!params.length) {
        return false;
    }
    const serialNum = createSerialNum();
    cppSendRemoteMessageEmValue(params, serialNum);
}
// 更新输入类型参数
export function setUpdateInputType(emPluginV3, inputType) {
    if (!emPluginV3 || typeof inputType !== "string") {
        return false;
    }
    const params = getInputType(emPluginV3, inputType);
    const serialNum = createSerialNum();
    cppSendRemoteMessageInputType(params, serialNum);
}

/**
 * 获取消除人声参数
 * value为0到100整数 50-0开始消除人声伴奏不变 0消除所有人声
 * 50-100开始消除伴奏 人声不变 100消除所有伴奏
 * @param {Object} emPlugsV3
 * @param {value} value
 * @returns
 */
export function updateRemoveVocals(emPlugsV3, value) {
    if (!emPlugsV3 || typeof value !== "number") {
        return false;
    }
    const params = getRemoveVocals(emPlugsV3, value);
    const serialNum = createSerialNum();
    cppSendRemoteMessageVocals(params, serialNum);
}

// 根据升降调和电音升降调计算最终基调
function computeEmKeyNote(keynote, sharpFlatVal, emSharpFlatVal) {
    let sharpFlatKeynote = "";
    // 匹配升降调 同时计算伴奏升降调和电音升降
    const sharpFlatValue = parseInt(sharpFlatVal + emSharpFlatVal, 10) || 0;
    if (sharpFlatValue) {
        const index = keynoteConfig.findIndex((ele) => {
            return ele.matchValue.includes(keynote.newKeyNote);
        });
        if (index !== -1) {
            let i = (sharpFlatValue + index) % 12;
            i = i < 0 ? 12 + i : i;
            sharpFlatKeynote = keynoteConfig[i].ketnote;
        }
    }
    const emKeynote = sharpFlatKeynote || formatKeynote(keynote.newKeyNote);
    let emKey = "";
    let musicKeynote = "";
    if (keynote.newScale === "大调") {
        emKey = "Major";
        musicKeynote = `${emKeynote} Major(大调)`;
    } else if (keynote.newScale === "小调") {
        emKey = "Minor";
        musicKeynote = `${emKeynote} Minor(小调)`;
    } else {
        emKey = "Chromatic";
        musicKeynote = `${emKeynote} Chromatic(彩色调)`;
    }
    return {
        emKeynote: emKeynote,
        emKey: emKey,
        musicKeynote: musicKeynote,
    };
}

// 获取插件显示值
export function getPluginShowName(pluginName) {
    let showName = "";
    if (!pluginName) {
        return showName;
    }
    pluginConfig.forEach((ele) => {
        if (ele.matchName.includes(pluginName)) {
            showName = ele.name;
        }
    });
    return showName || pluginName;
}

// 将各种格式的音阶转为通用12个音阶
function formatKeynote(keyNote) {
    const found = keynoteConfig.find((ele) => {
        return ele.matchValue.includes(keyNote);
    });
    return found ? found.ketnote : keyNote;
}

// 一个计数器 每次调用自增 自增到极限之后归0
function createCounter(max = 1000) {
    let counter = 0;
    return function () {
        counter++;
        if (counter > max) {
            counter = 0;
        }
        return counter;
    };
}

// 获取播放器进程名称
export function getPlayerProcessName(player) {
    const found = playerList.find((ele) => ele.player === player);
    return found && found.processName;
}
// 获取播放器Name
export function getPlayerName(player) {
    const found = playerList.find((ele) => ele.player === player);
    return found && found.name;
}

// 筛选要调整的电音精调参数
export function filterTargetEmValue(pageset, emValue) {
    const result = {};
    if (!pageset || !pageset.emFineTune || !emValue) {
        return result;
    }
    if (pageset.emRate && typeof emValue.rate === "number") {
        result.rate = emValue.rate;
    }
    if (pageset.emHumanize && typeof emValue.humanize === "number") {
        result.humanize = emValue.humanize;
    }
    if (pageset.emFlex && typeof emValue.flex === "number") {
        result.flex = emValue.flex;
    }
    if (pageset.emVibrato && typeof emValue.vibrato === "number") {
        result.vibrato = emValue.vibrato;
    }
    if (pageset.emTracking && typeof emValue.tracking === "number") {
        result.tracking = emValue.tracking;
    }
    return result;
}

// 通过AI分析的基调序号还原基调
export function getAiEmByIndex(index, framePos) {
    const found = AiEmList.find((ele) => ele.index === index);
    if (!found) {
        return;
    }
    return Object.assign(
        {
            framePos: framePos || 0,
        },
        found
    );
}
// 获取随机基调
export function getRandomEm(framePos) {
    const randomIndex = Math.floor(Math.random() * (24 - 1 + 1)) + 1;
    return getAiEmByIndex(randomIndex, framePos);
}

// 去掉字符串首尾空格
export function trimFirstAndLastSpace(str) {
    if (typeof str !== "string") {
        return str;
    }
    return str.trim();
}

// 本地基调在云端基调的序号
export function findIndexInEmusics(localEm, emusics) {
    if (!Array.isArray(emusics) || !Array.isArray(localEm)) {
        return -1;
    }
    if (localEm.length < 2) {
        return 0;
    }
    const index = emusics.findIndex((ele) => {
        return ele.newKeyNote === localEm[0] && ele.newScale === localEm[1];
    });
    return index;
}

// 格式化电音参数 通过取值范围

export function formatEmValByRange(value, range) {
    const decimalPlaces = (range.step.toString().split(".")[1] || "").length;
    const formattedNumber = parseFloat(value).toFixed(decimalPlaces);
    const v = parseFloat(formattedNumber);
    if (isNaN(v)) {
        return range.min;
    }
    return v < range.min ? range.min : v > range.max ? range.max : v;
}

// 比较基调的歌曲信息和播放器的歌曲信息是否一致、忽略常见的符合和空格
//
// 放宽过的规则（原来是歌名+歌手都必须完全相等，差一个字就当不匹配）：
// 不匹配的后果是 setEmByLocalDb 里直接把「AI 优先」打开，AI 分析结果会盖掉库里的基调，
// 所以这里宁可宽松一点：
//   ① 歌名必须一致（这条不放宽）
//   ② 播放器没上报歌手 → 只按歌名认（原来这种情况永远对不上，整首必然走 AI）
//   ③ 播放器报了歌手 → 库里的歌手相等 / 互相包含都算一致
//      （合唱版写成「h3R3刘清云」、多歌手写成「A / B」这类都能对上）
//   ④ 库里这条没歌手、播放器报了歌手 → 不认（没法核实是不是同一首歌）
export function compareEmAndPlayerdata(em, playerData) {
    if (typeof em !== "object" || typeof playerData !== "object") {
        return false;
    }
    const emName = removeSpacesAndSymbols(em.musicName);
    const pdName = removeSpacesAndSymbols(playerData.musicName);
    if (!emName || emName !== pdName) {
        return false;
    }
    const emAuthor = removeSpacesAndSymbols(em.musicAuthor);
    const pdAuthor = removeSpacesAndSymbols(playerData.musicAuthor);
    if (!pdAuthor) {
        return true;
    }
    if (!emAuthor) {
        return false;
    }
    return (
        emAuthor === pdAuthor ||
        emAuthor.indexOf(pdAuthor) !== -1 ||
        pdAuthor.indexOf(emAuthor) !== -1
    );
}

function removeSpacesAndSymbols(str) {
    if (typeof str !== "string") {
        return "";
    }
    // 定义要删除的符号集合，包括空格和一些常见的中英文标点符号
    const punctuation =
        "，。！？；：、‘’“”‘’()《》【】{}[]（）-_=+*/\\|@^~#$%&.,;:?<>\"'`";
    let result = "";
    // 遍历字符串的每个字符
    for (let i = 0; i < str.length; i++) {
        const char = str[i];
        // 检查字符是否是空格或定义的标点符号集合中的一个
        if (char !== " " && punctuation.indexOf(char) === -1) {
            // 如果不是，则将其添加到结果字符串中
            result += char;
        }
    }
    return result.toLowerCase();
}

/**
 * 检测两个title是否为滚动式消息
 * 通过判断最长公共子串占比来识别
 * @param {String} newTitle 新收到的title
 * @param {String} oldTitle 之前存储的title
 * @param {Number} threshold 公共子串占比阈值，默认0.5 (50%)
 * @returns {Boolean} 是否为滚动消息
 */
export function isScrollingTitle(newTitle, oldTitle, threshold = 0.5) {
    if (!oldTitle || !newTitle) {
        return false;
    }
    
    // 如果两个title完全相同，不是滚动消息
    if (newTitle === oldTitle) {
        return false;
    }
    
    // 寻找最长公共子串
    let maxMatch = 0;
    const newLen = newTitle.length;
    const oldLen = oldTitle.length;
    
    for (let i = 0; i < oldLen; i++) {
        for (let j = 0; j < newLen; j++) {
            let k = 0;
            while (
                i + k < oldLen &&
                j + k < newLen &&
                oldTitle[i + k] === newTitle[j + k]
            ) {
                k++;
            }
            maxMatch = Math.max(maxMatch, k);
        }
    }
    
    // 如果公共子串长度超过较长的字符串的指定比例，认为是滚动消息
    const minThreshold = Math.max(oldLen, newLen) * threshold;
    return maxMatch >= minThreshold;
}

// 获取联动调节要调节的对应参数
export function getEmValueByLinkage(val, emLinkage, range) {
    const result = {
        rate: "",
        humanize: "",
        vibrato: "",
        flex: "",
        tracking: "",
    };
    for (const key in result) {
        if (emLinkage[key] && emLinkage[key].isLink) {
            const { start, end } = emLinkage[key];
            if (start < end) {
                const v = start + (end - start) * (val / 100);
                result[key] = formatEmValByRange(v, range[key]);
            } else {
                const v = start - (start - end) * (val / 100);
                result[key] = formatEmValByRange(v, range[key]);
            }
        }
    }
    return result;
}
